import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { TrendingDown, TrendingUp } from "lucide-react";
import { DefinitionTooltip } from "src/components/common";
import type {
  OrganicSocialPlatform,
  SocialTotals,
} from "../../../data/organicSocialMockData";
import { getOrganicPlatformMetricDefinitionInfo } from "../organicMediaDefinitions";
import { SOCIAL_KPI_DEFINITIONS } from "./socialReportConfig";
import { formatSocialValue, socialChangePercent } from "./socialReportFormat";

interface SocialReportKpiCardsProps {
  platform: OrganicSocialPlatform;
  totals: SocialTotals;
  priorTotals: SocialTotals;
}

const SocialReportKpiCards: FC<SocialReportKpiCardsProps> = ({
  platform,
  totals,
  priorTotals,
}) => {
  const { t, i18n } = useTranslation();

  return (
    <div className="organic-content-report__kpis">
      {SOCIAL_KPI_DEFINITIONS.map((kpi, index) => {
        const Icon = kpi.icon;
        const label = t(kpi.labelKey);
        const change = socialChangePercent(totals[kpi.id], priorTotals[kpi.id]);
        const hasChange = typeof change === "number" && change !== 0;

        return (
          <div key={kpi.id} className="organic-content-card">
            <div className="organic-content-card__header">
              <div>
                <div className="organic-content-card__title-row">
                  <h3>{label}</h3>
                  <DefinitionTooltip
                    label={label}
                    info={getOrganicPlatformMetricDefinitionInfo(
                      platform,
                      kpi.helpLeaf,
                      label,
                      t
                    )}
                    align={index >= 2 ? "end" : "start"}
                    formulaTitle={t("organicMedia.common.formula")}
                  />
                </div>
                <div className="organic-content-card__value">
                  {formatSocialValue(totals[kpi.id], kpi.format, i18n.language)}
                </div>
              </div>
              <div className="organic-content-card__icon" aria-hidden>
                <Icon size={20} />
              </div>
            </div>
            <div className="organic-content-card__footer">
              {hasChange ? (
                <div className="organic-content-card__period">
                  <span
                    className={`organic-content-card__change organic-content-card__change--${
                      change > 0 ? "positive" : "negative"
                    }`}
                  >
                    {change > 0 ? (
                      <TrendingUp size={13} aria-hidden />
                    ) : (
                      <TrendingDown size={13} aria-hidden />
                    )}
                    {formatSocialValue(change, "percent", i18n.language)}
                  </span>
                  <span>{t("organicMedia.social.vsPriorPeriod")}</span>
                </div>
              ) : null}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default SocialReportKpiCards;
